import React from "react";
import { Card, CardBody, Col, Row, Table } from "reactstrap";
import { Link } from "react-router-dom";
import { DateOnly, DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import { PurchaseOrderStatusMap } from "../constants/purchase-order.constants";
import { usePurchaseOrderOverview } from "./PurchaseOrderOverviewContext";
import { formatCurrency } from "utils/currency";

const OverviewTab = () => {
  const { purchaseOrder: record } = usePurchaseOrderOverview();
  if (!record) return null;

  const st = record.status;
  const creator = record.creator;
  const approver = record.approver;
  const creatorDisplay = creator
    ? [creator.firstName, creator.lastName].filter(Boolean).join(" ") ||
      creator.email ||
      null
    : null;
  const approverDisplay = approver
    ? [approver.firstName, approver.lastName].filter(Boolean).join(" ") ||
      approver.email ||
      null
    : null;
  const notesText = record.notes ?? record.remarks;

  const variance =
    record.totalAmount != null && record.actualAmount != null
      ? record.actualAmount - record.totalAmount
      : null;

  return (
    <Row className="gy-3">
      <Col xl={8}>
        <Card>
          <CardBody>
            <div className="text-muted">
              <h6 className="mb-3 text-uppercase">Order info</h6>
              <div className="table-responsive">
                <Table className="table-borderless mb-0">
                  <tbody>
                    <tr>
                      <th
                        className="ps-0"
                        scope="row"
                        style={{ width: "180px" }}
                      >
                        Code :
                      </th>
                      <td className="text-muted">{record.code ?? "—"}</td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Supplier :
                      </th>
                      <td className="text-muted">
                        {record.supplier ? (
                          <Link
                            to={`/inventory-management/suppliers/${record.supplier.id}`}
                            className="link fw-medium"
                          >
                            {[record.supplier.code, record.supplier.name]
                              .filter(Boolean)
                              .join(" · ")}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Order date :
                      </th>
                      <td className="text-muted">
                        {record.orderDate ? (
                          <DateOnly date={record.orderDate} />
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Expected date :
                      </th>
                      <td className="text-muted">
                        {record.expectedDate ? (
                          <DateOnly date={record.expectedDate} />
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    {record.materialRequests &&
                    record.materialRequests.length > 0 ? (
                      <tr>
                        <th className="ps-0" scope="row">
                          Material requests :
                        </th>
                        <td className="text-muted">
                          <div className="d-flex flex-wrap gap-2">
                            {record.materialRequests.map((mr) =>
                              mr.id ? (
                                <Link
                                  key={mr.id}
                                  to={`/inventory-management/material-requests/${mr.id}`}
                                  className="link fw-medium"
                                >
                                  {mr.code ?? `MR #${mr.id}`}
                                </Link>
                              ) : null,
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                    <tr>
                      <th className="ps-0" scope="row">
                        Notes :
                      </th>
                      <td className="text-muted">{notesText ?? "—"}</td>
                    </tr>
                    {creator ? (
                      <tr>
                        <th className="ps-0" scope="row">
                          Created by :
                        </th>
                        <td className="text-muted">{creatorDisplay ?? "—"}</td>
                      </tr>
                    ) : null}
                    {record.approvedAt ? (
                      <tr>
                        <th className="ps-0" scope="row">
                          Approved :
                        </th>
                        <td className="text-muted">
                          <DateTime date={record.approvedAt} />
                          {approverDisplay ? (
                            <span className="text-muted ms-2">
                              ({approverDisplay})
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    ) : null}
                    {record.cancelledAt ? (
                      <tr>
                        <th className="ps-0" scope="row">
                          Cancelled :
                        </th>
                        <td className="text-muted">
                          <DateTime date={record.cancelledAt} />
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </Table>
              </div>

              <div className="pt-3 border-top border-top-dashed mt-4">
                <Row className="gy-3">
                  {record.created_at ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">Created</p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={record.created_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}
                  {record.updated_at ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Last updated
                        </p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={record.updated_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}
                  <Col lg={3} sm={6}>
                    <div>
                      <p className="mb-2 text-uppercase fw-medium">Status :</p>
                      <h5 className="fs-14 mb-0">
                        <StatusBadge value={st} map={PurchaseOrderStatusMap} />
                      </h5>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>

      <Col xl={4}>
        <Card>
          <CardBody>
            <h6 className="mb-4 text-uppercase">Financials</h6>
            <div className="d-flex flex-column gap-4">
              <div className="p-3 rounded bg-light bg-opacity-50 border">
                <p className="text-muted small text-uppercase fw-semibold mb-1">
                  <i className="ri-price-tag-3-line me-1 align-bottom" />
                  Estimated total
                </p>
                <h3 className="mb-0 fw-semibold">
                  {record.totalAmount != null ? (
                    formatCurrency(record.totalAmount)
                  ) : (
                    <span className="text-muted fs-5">—</span>
                  )}
                </h3>
                <p className="text-muted small mb-0 mt-1">
                  Sum of line item rates × quantities
                </p>
              </div>

              <div
                className={`p-3 rounded border ${record.actualAmount != null ? "bg-success bg-opacity-10 border-success border-opacity-25" : "bg-light bg-opacity-50"}`}
              >
                <p className="text-muted small text-uppercase fw-semibold mb-1">
                  <i className="ri-money-rupee-circle-line me-1 align-bottom" />
                  Actual spend
                </p>
                <h3
                  className={`mb-0 fw-semibold ${record.actualAmount != null ? "text-success" : ""}`}
                >
                  {record.actualAmount != null ? (
                    formatCurrency(record.actualAmount)
                  ) : (
                    <span className="text-muted fs-5">Not recorded</span>
                  )}
                </h3>
                {variance != null ? (
                  <p
                    className={`small mb-0 mt-1 ${variance > 0 ? "text-danger" : "text-success"}`}
                  >
                    <i
                      className={`me-1 align-bottom ${variance > 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"}`}
                    />
                    {formatCurrency(Math.abs(variance))}{" "}
                    {variance > 0 ? "over estimate" : "under estimate"}
                  </p>
                ) : (
                  <p className="text-muted small mb-0 mt-1">
                    Recorded after fulfillment
                  </p>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default OverviewTab;
